import React, { useState, useEffect, useCallback } from 'react';

const STATUS_LABELS = {
  PENDING_ASSIGNMENT: 'Submitted',
  ASSIGNED: 'In review',
  IN_REVIEW: 'Expert reviewing',
  RESPONDED: 'Responded',
};

const STATUS_COLORS = {
  PENDING_ASSIGNMENT: { bg: 'rgba(100,100,100,0.08)', color: 'var(--text-secondary)', border: 'var(--border)' },
  ASSIGNED: { bg: 'rgba(0,194,124,0.08)', color: 'var(--accent)', border: 'rgba(0,194,124,0.25)' },
  IN_REVIEW: { bg: 'rgba(0,194,124,0.12)', color: 'var(--accent)', border: 'rgba(0,194,124,0.3)' },
  RESPONDED: { bg: 'rgba(52,199,89,0.1)', color: '#1a8c38', border: 'rgba(52,199,89,0.3)' },
};

const DOMAIN_LABELS = {
  FINANCE: 'Finance',
  LEGAL: 'Legal',
};

const MatterStatus = ({ user, navigateTo, goBack, activeProfileType }) => {
  const profileType = activeProfileType || (user?.accountType === 'personal' ? 'PERSONAL' : 'BUSINESS');

  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  // Review state: { [matterId]: { rating, comment, submitting, submitted } }
  const [reviews, setReviews] = useState({});

  const fetchMatters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/profiles?action=listMatters&profileType=${profileType}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Failed to load matters');
      const data = await res.json();
      setMatters(data.matters || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [profileType]);

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  const handleStarClick = (matterId, star) => {
    setReviews(prev => ({ ...prev, [matterId]: { ...(prev[matterId] || {}), rating: star } }));
  };

  const handleReviewSubmit = async (matter) => {
    const rev = reviews[matter.matterId] || {};
    if (!rev.rating) return;
    setReviews(prev => ({ ...prev, [matter.matterId]: { ...rev, submitting: true } }));
    try {
      const res = await fetch('/api/profiles?action=submitReview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          professionalId: matter.assignedProfessionalId,
          matterId: matter.matterId,
          rating: rev.rating,
          comment: rev.comment || '',
        }),
      });
      if (res.ok) {
        setReviews(prev => ({ ...prev, [matter.matterId]: { ...rev, submitting: false, submitted: true } }));
      }
    } catch {
      setReviews(prev => ({ ...prev, [matter.matterId]: { ...rev, submitting: false } }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Header */}
        <button
          onClick={() => goBack()}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '14px', cursor: 'pointer', padding: 0, marginBottom: '16px', fontFamily: 'inherit' }}
        >
          ← Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '30px', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' }}>
              My Matters
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {profileType === 'PERSONAL' ? 'Personal' : 'Business'} profile
            </p>
          </div>
          <button
            onClick={() => navigateTo('professionals-directory')}
            style={{ padding: '10px 20px', borderRadius: 'var(--radius-control)', border: 'none', backgroundColor: 'var(--accent)', color: 'var(--bg)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            + New matter
          </button>
        </div>

        {/* States */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-tertiary)' }}>
            Loading matters…
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', borderRadius: 'var(--radius-control)', backgroundColor: '#fff2f2', border: '1px solid #ffd6d6', color: '#c00', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {!loading && !error && matters.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <p style={{ fontSize: '17px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
              No matters yet.
            </p>
            <button
              onClick={() => navigateTo('professionals-directory')}
              style={{ padding: '12px 28px', borderRadius: 'var(--radius-control)', border: 'none', backgroundColor: 'var(--accent)', color: 'var(--bg)', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Talk to an expert
            </button>
          </div>
        )}

        {/* Matter cards */}
        {!loading && !error && matters.length > 0 && (
          <div style={{ display: 'grid', gap: '12px' }}>
            {matters.map(matter => {
              const statusStyle = STATUS_COLORS[matter.status] || STATUS_COLORS.PENDING_ASSIGNMENT;
              const isExpanded = expanded === matter.matterId;

              return (
                <div
                  key={matter.matterId}
                  style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', backgroundColor: 'var(--bg)', overflow: 'hidden' }}
                >
                  {/* Card header */}
                  <button
                    onClick={() => setExpanded(isExpanded ? null : matter.matterId)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px' }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {DOMAIN_LABELS[matter.domain] || matter.domain} matter
                        </span>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', backgroundColor: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
                          {STATUS_LABELS[matter.status] || matter.status}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: 0 }}>
                        Submitted {formatDate(matter.createdAt)}
                      </p>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                      {isExpanded ? '▲' : '▼'}
                    </span>
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{ padding: '0 24px 24px', borderTop: '1px solid var(--border)' }}>
                      {matter.summarySnapshot && (
                        <div style={{ marginTop: '16px' }}>
                          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Summary</p>
                          <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{matter.summarySnapshot}</p>
                        </div>
                      )}

                      {matter.assignedProfessionalId && (
                        <div style={{ marginTop: '16px' }}>
                          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned to</p>
                          <p style={{ fontSize: '14px', color: 'var(--text-primary)', margin: 0 }}>{matter.assignedProfessionalId}</p>
                        </div>
                      )}

                      {matter.response && (
                        <div style={{ marginTop: '16px', padding: '16px', borderRadius: 'var(--radius-control)', backgroundColor: 'rgba(52,199,89,0.06)', border: '1px solid rgba(52,199,89,0.2)' }}>
                          <p style={{ fontSize: '12px', color: '#1a8c38', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Expert response</p>
                          <p style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{matter.response}</p>
                        </div>
                      )}

                      {/* Rating prompt — only for RESPONDED matters */}
                      {matter.status === 'RESPONDED' && matter.assignedProfessionalId && (() => {
                        const rev = reviews[matter.matterId] || {};
                        if (rev.submitted) {
                          return (
                            <div style={{ marginTop: '16px', padding: '14px 18px', borderRadius: '10px', backgroundColor: 'rgba(0,194,124,0.06)', border: '1px solid rgba(0,194,124,0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: 'var(--accent)', fontSize: '16px' }}>✓</span>
                              <span style={{ fontSize: '14px', color: 'var(--accent)', fontWeight: 600 }}>Thank you for your review!</span>
                            </div>
                          );
                        }
                        return (
                          <div style={{ marginTop: '16px', padding: '18px 20px', borderRadius: '10px', border: '1px solid var(--border)', backgroundColor: 'var(--bg-secondary)' }}>
                            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 12px' }}>How was your consultation?</p>
                            {/* Star selector */}
                            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
                              {[1, 2, 3, 4, 5].map(star => (
                                <button
                                  key={star}
                                  onClick={() => handleStarClick(matter.matterId, star)}
                                  style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    fontSize: '26px', padding: '0 2px', lineHeight: 1,
                                    color: (rev.rating || 0) >= star ? '#F59E0B' : 'var(--border)',
                                    transition: 'color 0.1s',
                                  }}
                                >★</button>
                              ))}
                              {rev.rating && (
                                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', alignSelf: 'center', marginLeft: '4px' }}>
                                  {['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'][rev.rating]}
                                </span>
                              )}
                            </div>
                            {rev.rating && (
                              <>
                                <textarea
                                  placeholder="Add a comment (optional)"
                                  value={rev.comment || ''}
                                  onChange={e => setReviews(prev => ({ ...prev, [matter.matterId]: { ...rev, comment: e.target.value } }))}
                                  rows={2}
                                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', color: 'var(--text-primary)', backgroundColor: 'var(--bg)', resize: 'none', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
                                />
                                <button
                                  onClick={() => handleReviewSubmit(matter)}
                                  disabled={rev.submitting}
                                  style={{ padding: '9px 22px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--accent)', color: 'white', fontSize: '13px', fontWeight: 600, cursor: rev.submitting ? 'not-allowed' : 'pointer', opacity: rev.submitting ? 0.7 : 1, fontFamily: 'inherit' }}
                                >{rev.submitting ? 'Submitting…' : 'Submit review'}</button>
                              </>
                            )}
                          </div>
                        );
                      })()}

                      {/* Status flow */}
                      <div style={{ marginTop: '20px', display: 'flex', gap: '0', alignItems: 'center' }}>
                        {['PENDING_ASSIGNMENT', 'ASSIGNED', 'IN_REVIEW', 'RESPONDED'].map((s, i, arr) => {
                          const isActive = s === matter.status;
                          const isPast = arr.indexOf(matter.status) > i;
                          return (
                            <React.Fragment key={s}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: isActive ? 'var(--accent)' : isPast ? 'rgba(52,199,89,0.8)' : 'var(--bg-secondary)', border: `2px solid ${isActive ? 'var(--accent)' : isPast ? 'rgba(52,199,89,0.8)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', fontSize: '12px', color: (isActive || isPast) ? 'var(--bg)' : 'var(--text-tertiary)', fontWeight: 700 }}>
                                  {isPast ? '✓' : i + 1}
                                </div>
                                <p style={{ fontSize: '9px', color: isActive ? 'var(--accent)' : 'var(--text-tertiary)', marginTop: '4px', fontWeight: isActive ? 600 : 400, width: '60px', textAlign: 'center' }}>
                                  {STATUS_LABELS[s]}
                                </p>
                              </div>
                              {i < arr.length - 1 && (
                                <div style={{ flex: 1, height: '2px', backgroundColor: isPast ? 'rgba(52,199,89,0.4)' : 'var(--border)', margin: '0 2px', marginBottom: '18px' }} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatterStatus;
